import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'

import { getSubSearchParam } from '../../../../../utils/route'
import {
  REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_PARAM_KEY,
  REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ALL,
  REQUEST_PROFILE_TAB_EMAILS_FOLDER_ALL,
  REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY,
  REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY,
} from '../../RequestProfileTabEmails.constants'
import {
  getRequestProfileTabEmailsActiveSubTabFromFilters,
  getRequestProfileTabEmailsDefaultFilters,
  getRequestProfileTabEmailsFiltersWithSubTab,
  getRequestProfileTabEmailsSearchParamsWithSubTab,
  getRequestProfileTabEmailsSubTabFromSearchParams,
} from '../../RequestProfileTabEmails.utils'

// Keeps the Emails sub-tab (folder + audience) in sync with the URL so it is
// shareable. Deep links are applied by seeding the initial filter load (see
// `getRequestProfileTabEmailsInitialFilters` passed as `transformInitialFilters`
// to `useAsyncTable`), so this hook only (a) writes the restored default to the
// URL once on entry when it is absent, and (b) on an explicit sub-tab change,
// updates the table filters (which persists, matching the previous behaviour)
// and mirrors the choice to the URL.
export function useRequestProfileTabEmailsSubTabUrl({
  filterValues,
  handleSetFilterValues,
  isPreview = false,
}) {
  const [searchParams, setSearchParams] = useSearchParams()

  const hasWrittenDefaultRef = useRef(false)

  const urlSubTab =
    getRequestProfileTabEmailsSubTabFromSearchParams(searchParams)
  const urlContactType =
    urlSubTab[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY]
  const urlFolder = urlSubTab[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY]

  const rawFolder = getSubSearchParam(searchParams)
  const rawContactType = searchParams.get(
    REQUEST_PROFILE_TAB_EMAILS_AUDIENCE_PARAM_KEY
  )
  const hasInvalidSubTabParam =
    (Boolean(rawFolder) && !urlFolder) ||
    (Boolean(rawContactType) && !urlContactType)

  function writeSubTabToUrl({ contactType, folder }) {
    if (!isPreview) {
      setSearchParams(
        prev =>
          getRequestProfileTabEmailsSearchParamsWithSubTab(prev, {
            contactType,
            folder,
          }),
        { replace: true }
      )
    }
  }

  // On entry, reflect the active sub-tab in the URL once when it isn't fully
  // represented there — either no valid params are present, or a present-but-
  // invalid param needs to be normalized away — so the URL is complete and
  // shareable.
  useEffect(() => {
    const shouldWriteSubTab =
      filterValues != null &&
      !hasWrittenDefaultRef.current &&
      (hasInvalidSubTabParam || (!urlFolder && !urlContactType))

    if (shouldWriteSubTab) {
      hasWrittenDefaultRef.current = true

      const current =
        getRequestProfileTabEmailsActiveSubTabFromFilters(filterValues)

      writeSubTabToUrl({
        contactType:
          current[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY],
        folder: current[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY],
      })
    }
  }, [filterValues, hasInvalidSubTabParam, urlContactType, urlFolder])

  function handleSetActiveSubTab(nextSubTab) {
    const contactType =
      nextSubTab[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_CONTACT_TYPE_KEY]
    const folder = nextSubTab[REQUEST_PROFILE_TAB_EMAILS_SUB_TAB_FOLDER_KEY]

    handleSetFilterValues(
      getRequestProfileTabEmailsFiltersWithSubTab({
        contactType,
        filterValues,
        subMenu: folder,
      })
    )

    writeSubTabToUrl({ contactType, folder })
  }

  function handleResetSubTabAndFilters() {
    handleSetFilterValues(getRequestProfileTabEmailsDefaultFilters())

    writeSubTabToUrl({
      contactType: REQUEST_PROFILE_TAB_EMAILS_CONTACT_TYPE_ALL,
      folder: REQUEST_PROFILE_TAB_EMAILS_FOLDER_ALL,
    })
  }

  return {
    handleResetSubTabAndFilters,
    handleSetActiveSubTab,
  }
}
